import React, { useState } from "react";
import {
    Button,
    Typography,
    TablePagination,
    IconButton,
    TextField,
    InputAdornment,
    Radio,
    RadioGroup,
    Menu,
    Stack,
    Box,
    LinearProgress,
    Tooltip
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { useQuery } from "@tanstack/react-query";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import { DeleteDialog } from "../../utils/CommonComonents/DeleteDialog";
import { UsersTable } from "./UserTable";
import {
    isMbxUserAdmin,
    isMbxUserVerifiedStaff,
    mbxUserAtom
} from "../../atoms/atoms";
import { providerScheduleByCompany } from "../../modules/ScheduleQueries";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import {
    visibleRows,
    tableConfigAtom,
    visibleRowsPaginated,
    paginationAtom,
    setPageAtom,
    setPerPageAtom,
    openUserDialogAtom,
    selectedUserIdAtom
} from "../../atoms/usersTable";
import {
    isLoadingUsersAtom,
    loadingUsersProgressAtom
} from "../../atoms/atoms";
import { useAtom } from "jotai";
import { allSchedulesAtom } from "../../atoms/atoms";
import ExportUsersList from "../../forms/ExportUsersList";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import {
    tableTheme,
    tableConfigTheme,
    dropDownMenuTheme,
    paginationTheme
} from "../../styles/ListViewTheme.js";
import {
    ROLE_OPTIONS,
    AVAILABILITY_OPTIONS,
    STATUS_OPTIONS
} from "../../atoms/usersTable";
import { Link } from "react-router-dom";
import { execReadBySortkey } from "../../modules/DBService";

const STATUS_OPTIONS_LABELS = {
    [STATUS_OPTIONS.ALL]: "All",
    [STATUS_OPTIONS.ACTIVE]: "Active",
    [STATUS_OPTIONS.INACTIVE]: "Inactive"
};
const ROLE_OPTIONS_LABELS = {
    [ROLE_OPTIONS.ALL]: "All roles",
    [ROLE_OPTIONS.ADMINS]: "Admins",
    [ROLE_OPTIONS.MANAGERS]: "Managers",
    [ROLE_OPTIONS.PROVIDERS]: "Providers"
};
const AVAILABILITY_OPTIONS_LABELS = {
    [AVAILABILITY_OPTIONS.ALL]: "All ",
    [AVAILABILITY_OPTIONS.ACTIVE_SCHEDULE]: "Available",
    [AVAILABILITY_OPTIONS.NO_ACTIVE_SCHEDULE]: "Not available"
};

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];
export const UsersPage = () => {
    const [mbxUser] = useAtom(mbxUserAtom);
    const { companyId } = mbxUser;
    const [displayUsers] = useAtom(visibleRowsPaginated);
    const [allViewableUsers] = useAtom(visibleRows);
    const [loadingUsersProgress] = useAtom(loadingUsersProgressAtom);
    const [isLoadingUsers] = useAtom(isLoadingUsersAtom);
    const [, setAllSchedules] = useAtom(allSchedulesAtom);
    const [deleteDialogOpen] = useState(false);
    const [paginationConfig] = useAtom(paginationAtom);
    const [, setPerPageAction] = useAtom(setPerPageAtom);
    const [, setPageAction] = useAtom(setPageAtom);
    const { page, perPage } = paginationConfig;
    const [, setOpenUserDialog] = useAtom(openUserDialogAtom);
    const theme = useTheme();

    const { isLoading: isSchedulesLoading } = useQuery({
        queryKey: ["get-schedules"],
        queryFn: async () => {
            try {
                let { items, errors } = await execReadBySortkey({
                    opname: "providerScheduleByCompany",
                    op: providerScheduleByCompany,
                    id: {
                        companyId: companyId
                    },
                    skey: {
                        scheduleinfo: { beginsWith: "SB" }
                    },
                    filter: { deleted: { ne: true }, active: { eq: true } },
                    limit: 500
                });
                if (errors) throw errors;
                return items;
            } catch (e) {
                console.log(e);
            }
        },
        onSuccess: (schedulesData) => {
            setAllSchedules(schedulesData);
        }
    });

    return (
        <>
            {isLoadingUsers || isSchedulesLoading ? (
                <Box sx={{ width: "100%" }}>
                    <LinearProgress
                        variant="buffer"
                        valueBuffer={loadingUsersProgress}
                        value={loadingUsersProgress}
                    />
                </Box>
            ) : (
                <ThemeProvider theme={tableTheme(theme)}>
                    <TableHeader
                        onOpenUserDialog={() => setOpenUserDialog(true)}
                    />

                    <>
                        <Box
                            sx={{
                                flexGrow: 1,
                                display: "flex",
                                flexDirection: "column"
                            }}
                        >
                            {displayUsers.length === 0 ? (
                                <>
                                    <UsersTable users={[]} />
                                    <Box
                                        sx={{
                                            flexGrow: 1,
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            height: "70vh"
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            color={theme.palette.text.secondary}
                                        >
                                            No users found
                                        </Typography>
                                    </Box>
                                </>
                            ) : (
                                <UsersTable users={displayUsers} />
                            )}
                        </Box>

                        <ThemeProvider theme={paginationTheme(theme)}>
                            <Box
                                sx={{
                                    bottom: 0,
                                    backgroundColor:
                                        theme.palette.background.paper
                                }}
                            >
                                <TablePagination
                                    count={allViewableUsers?.length || 0}
                                    component={"div"}
                                    page={page}
                                    onPageChange={(e, page) =>
                                        setPageAction(page)
                                    }
                                    onRowsPerPageChange={({
                                        target: { value: perPage }
                                    }) => setPerPageAction(perPage)}
                                    rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                                    rowsPerPage={perPage}
                                    labelRowsPerPage=""
                                />
                            </Box>
                        </ThemeProvider>
                    </>
                    <DeleteDialog open={deleteDialogOpen} />
                </ThemeProvider>
            )}
        </>
    );
};

const TableHeader = ({ onOpenUserDialog }) => {
    const [tableConfig, setTableConfig] = useAtom(tableConfigAtom);
    const [isAdminLoggedIn] = useAtom(isMbxUserAdmin);
    const [isVerifiedStaff] = useAtom(isMbxUserVerifiedStaff);
    const { searchTerm } = tableConfig;
    const [startExport, setStartExport] = useState(false);
    const [, setSelectedUserId] = useAtom(selectedUserIdAtom);
    const themePalette = useTheme().palette;
    const handleClearClick = () => {
        setTableConfig({
            ...tableConfig,
            searchTerm: ""
        });
    };

    const handleExportClick = () => {
        setStartExport(true);
    };

    const handleExportComplete = () => {
        setStartExport(false);
    };

    return (
        <ThemeProvider theme={tableConfigTheme(themePalette)}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ marginBottom: "15px" }}
            >
                <Stack
                    direction="row"
                    gap={"12px"}
                    sx={{ textAlign: "center", alignItems: "center" }}
                >
                    <Typography variant="h1">Users </Typography>
                    {isAdminLoggedIn && (
                        <Tooltip title="Export">
                            <CloudDownloadIcon
                                color="primary"
                                onClick={handleExportClick}
                            />
                        </Tooltip>
                    )}
                    {startExport && (
                        <ExportUsersList
                            onExportComplete={handleExportComplete}
                        />
                    )}
                </Stack>
                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    spacing={2}
                >
                    {isAdminLoggedIn && (
                        <Link align="center" underline="hover" to="/skills">
                            <Typography variant="body2" color="primary">
                                <b>Manage skills</b>
                            </Typography>
                        </Link>
                    )}
                    <FiltersMenu />
                    <TextField
                        id="search"
                        sx={{
                            maxWidth: "200px",
                            "& .MuiInputBase-root": {
                                paddingLeft: "14px !important"
                            }
                        }}
                        value={searchTerm}
                        placeholder="Search"
                        onChange={({ target: { value } }) =>
                            setTableConfig({
                                ...tableConfig,
                                searchTerm: value
                            })
                        }
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon
                                        sx={{
                                            width: "18px",
                                            height: "18px",
                                            color: `${themePalette.helperText.main}`
                                        }}
                                    />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm &&
                                searchTerm.length > 0 && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            aria-label="clear"
                                            onClick={handleClearClick}
                                        >
                                            <ClearIcon
                                                sx={{
                                                    width: "18px",
                                                    height: "18px"
                                                }}
                                            />
                                        </IconButton>
                                    </InputAdornment>
                                )
                        }}
                    />
                    <Button
                        textTransform="uppercase"
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon sx={{ color: "white" }} />}
                        onClick={() => {
                            setSelectedUserId(null);
                            onOpenUserDialog();
                        }}
                    >
                        <Typography
                            variant="button"
                            sx={{ fontWeight: "600", color: "white" }}
                        >
                            {" "}
                            Add User
                        </Typography>
                    </Button>
                </Stack>
            </Stack>
        </ThemeProvider>
    );
};

export default function FiltersMenu() {
    const [tableConfig, setTableConfig] = useAtom(tableConfigAtom);
    const { statusFilter, roleFilter, availabilityFilter } = tableConfig;
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const theme = useTheme();
    return (
        <>
            <TextField
                sx={{ maxWidth: "175px" }}
                value={"Filters"}
                onClick={handleClick}
                fullWidth
                InputProps={{
                    sx: {
                        color: theme.palette.text.main,
                        fontSize: "14px",
                        cursor: "pointer",
                        ...(open && {
                            "& fieldset": {
                                border: `2px solid ${theme.palette.primary.main}`
                            }
                        })
                    },
                    endAdornment: (
                        <InputAdornment position="start">
                            {open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                        </InputAdornment>
                    ),
                    readOnly: true
                }}
                inputProps={{
                    sx: {
                        cursor: "pointer"
                    }
                }}
            />
            <ThemeProvider theme={dropDownMenuTheme(theme)}>
                <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={open}
                    fullWidth
                    onClick={handleClose}
                    onClose={handleClose}
                    MenuListProps={{
                        "aria-labelledby": "basic-button",
                        sx: {
                            width: anchorEl && anchorEl.offsetWidth,
                            padding: "10px 15px"
                        }
                    }}
                >
                    <FormControl>
                        <FormLabel id="demo-radio-buttons-group-label">
                            <Typography variant="subHeading">Role</Typography>
                        </FormLabel>
                        <RadioGroup
                            aria-labelledby="demo-radio-buttons-group-label"
                            defaultValue={ROLE_OPTIONS.ALL}
                            name="radio-buttons-group"
                            value={roleFilter}
                            onChange={({ target: { value } }) =>
                                setTableConfig({
                                    ...tableConfig,
                                    roleFilter: value
                                })
                            }
                        >
                            {Object.values(ROLE_OPTIONS).map((option) => (
                                <FormControlLabel
                                    key={option}
                                    value={option}
                                    control={<Radio disableRipple />}
                                    label={ROLE_OPTIONS_LABELS[option]}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                    <FormControl>
                        <FormLabel id="demo-radio-buttons-group-label">
                            <Typography variant="subHeading">
                                Availability
                            </Typography>
                        </FormLabel>
                        <RadioGroup
                            aria-labelledby="demo-radio-buttons-group-label"
                            defaultValue={AVAILABILITY_OPTIONS.ALL}
                            name="radio-buttons-group"
                            value={availabilityFilter}
                            onChange={({ target: { value } }) =>
                                setTableConfig({
                                    ...tableConfig,
                                    availabilityFilter: value
                                })
                            }
                        >
                            {Object.values(AVAILABILITY_OPTIONS).map(
                                (option) => (
                                    <FormControlLabel
                                        key={option}
                                        value={option}
                                        control={<Radio disableRipple />}
                                        label={
                                            AVAILABILITY_OPTIONS_LABELS[option]
                                        }
                                    />
                                )
                            )}
                        </RadioGroup>
                    </FormControl>
                    <FormControl>
                        <FormLabel id="demo-radio-buttons-group-label">
                            <Typography variant="subHeading">Active</Typography>
                        </FormLabel>
                        <RadioGroup
                            aria-labelledby="demo-radio-buttons-group-label"
                            defaultValue={STATUS_OPTIONS.ACTIVE}
                            name="radio-buttons-group"
                            value={statusFilter}
                            onChange={({ target: { value } }) =>
                                setTableConfig({
                                    ...tableConfig,
                                    statusFilter: value
                                })
                            }
                        >
                            {Object.values(STATUS_OPTIONS).map((option) => (
                                <FormControlLabel
                                    key={option}
                                    value={option}
                                    control={<Radio disableRipple />}
                                    label={STATUS_OPTIONS_LABELS[option]}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                </Menu>
            </ThemeProvider>
        </>
    );
}
